import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '@services/api';
import { getInstrumentCurrencySymbol, ticker } from '@utils/helpers';
import PriceChange from './PriceChange';
import RangeSelector from './RangeSelector';

interface MoverStock {
  symbol: string;
  name: string;
  price: number;
  change_percent: number;
  volume: number;
  formatted_volume: string;
}

interface MoversResponse {
  market: string;
  gainers: MoverStock[];
  losers: MoverStock[];
  most_active: MoverStock[];
}

type Tab = 'gainers' | 'losers' | 'most_active';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'gainers', label: 'Yükselenler' },
  { key: 'losers', label: 'Düşenler' },
  { key: 'most_active', label: 'En Aktif' },
];

interface MarketMoversProps {
  market: 'bist' | 'us';
  excludeSymbol?: string;
}

const MarketMovers: React.FC<MarketMoversProps> = ({ market, excludeSymbol }) => {
  // Tri-state: undefined = loading, null = failed/empty, object = loaded
  const [data, setData] = useState<MoversResponse | null | undefined>(undefined);
  const [tab, setTab] = useState<Tab>('gainers');

  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    apiClient
      .get<MoversResponse>('/financial/trending/movers/', { params: { market, limit: 5 } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [market]);

  // A sidebar extra: when the API has nothing, take no space instead of erroring
  if (data === null) return null;

  // The cached response is market-wide; drop the page's own row client-side.
  const exclude = excludeSymbol?.toUpperCase();
  const stocks = (data?.[tab] ?? []).filter((s) => s.symbol.toUpperCase() !== exclude);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-400">Piyasa Hareketleri</span>
        <span className="chip bg-white/[0.06] text-gray-300 uppercase tracking-wide">{market === 'bist' ? 'BIST' : 'ABD'}</span>
      </div>
      <RangeSelector
        options={TABS.map(({ key, label }) => ({ value: key, label }))}
        value={tab}
        onChange={setTab}
        className="mb-2 w-full"
        itemClassName="flex-1"
        ariaLabel="Piyasa hareketleri sekmeleri"
      />
      {data === undefined ? (
        <div className="animate-pulse space-y-1">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex justify-between px-2 py-2.5">
              <div className="h-3 bg-white/[0.06] rounded w-24"></div>
              <div className="h-3 bg-white/[0.06] rounded w-16"></div>
            </div>
          ))}
        </div>
      ) : stocks.length === 0 ? (
        <p className="text-[12px] text-gray-500 px-2 py-3">Şu anda listelenecek hisse yok.</p>
      ) : (
        <div>
          {stocks.map((stock) => (
            <Link
              key={stock.symbol}
              to={`/instrument/${encodeURIComponent(stock.symbol)}`}
              className="flex items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-white/[0.04] transition-colors"
            >
              <div className="min-w-0">
                <span className="block text-[13px] font-semibold text-gray-200">
                  {ticker(stock.symbol)}
                </span>
                <span className="block text-[11px] text-gray-500 truncate">
                  {tab === 'most_active' ? `Hacim ${stock.formatted_volume}` : stock.name}
                </span>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="block text-num text-[12px] text-gray-300 mb-0.5">
                  {getInstrumentCurrencySymbol(stock.symbol)}{stock.price.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                </span>
                <PriceChange value={stock.change_percent} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketMovers;
